import { BaseElement } from "@/components";
import { Auth } from "@saasbase-io/loginflow-websdk";
import { nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { html, unsafeStatic } from "lit/static-html.js";
import { OtpVariant } from "./otp.types";
import { NAMESPACE } from "@/constants";

@customElement("sb-otp")
export class SbOtp extends BaseElement {
  // --------------------------------------------------
  // PROPS
  // --------------------------------------------------
  static readonly ROOT = "otp";

  get _containerElement(): HTMLLIElement | null {
    return this.querySelector(
      `rtg-input-otp[part=${this.id ? this.id : "otp"}]`,
    );
  }

  @property({
    type: Boolean,
    attribute: "exclude-from-form",
    converter: {
      fromAttribute: (value: string | null) => {
        return value !== null && value !== "false";
      },
      toAttribute: (value: boolean) => (value ? "" : null),
    },
  })
  excludeFromForm = false;

  get isFormField() {
    return !this.excludeFromForm;
  }

  static readonly TAG = `${NAMESPACE}-${SbOtp.ROOT}`;

  @property({ type: Boolean })
  required = false;

  @property({ attribute: "required-error" })
  requiredError = "Code is required.";

  @property({ attribute: "incomplete-error" })
  incompleteError = "Please enter the complete verification code.";

  @property()
  variant: OtpVariant = "with_separator";

  @property({ type: Number })
  length = 6;

  @property({ type: Number })
  timeout = 300;

  @property({ attribute: "data-key" })
  dataKey = "";

  /**
   * Used by SbForm when autoSubmit=false.
   */
  @property()
  name = "";

  /**
   * Used by SbForm loading state.
   */
  @property({ type: Boolean })
  disabled = false;

  /**
   * If true:
   *   - behaves exactly like the current OTP component
   *   - directly calls Auth.processLoginflowEvent()
   *
   * If false:
   *   - behaves like a form field
   *   - value is collected by SbForm
   */
  @property({
    type: Boolean,
    attribute: "auto-submit",
    converter: {
      fromAttribute: (value: string | null) => {
        return value !== null && value !== "false";
      },
      toAttribute: (value: boolean) => (value ? "" : null),
    },
  })
  autoSubmit = false;

  @property()
  event = "";

  @property({ attribute: "resend-text" })
  resendText = "";

  @property({ attribute: "resend-button-text" })
  resendButtonText = "";

  @property({ attribute: "resend-event" })
  resendEvent = "";

  @property()
  id = "otp";

  @state()
  private _invalid = false;

  @state()
  private _error = "";

  // --------------------------------------------------
  // INTERNAL STATE
  // --------------------------------------------------

  @state()
  private _value = "";

  protected createRenderRoot() {
    return this;
  }

  // --------------------------------------------------
  // FORM FIELD API
  // --------------------------------------------------

  get value() {
    return this._value;
  }

  validate() {
    const value = this._value.trim();

    if (this.required && !value) {
      this._invalid = true;
      this._error = this.requiredError;
      return false;
    }

    if (value.length !== this.length) {
      this._invalid = true;
      this._error = this.incompleteError;
      return false;
    }

    this._invalid = false;
    this._error = "";

    return true;
  }

  // --------------------------------------------------
  // EVENTS
  // --------------------------------------------------

  private emit(name: string, detail: any) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }

  // --------------------------------------------------
  // OTP STRUCTURE
  // --------------------------------------------------

  private buildOTPContentHTML(): string {
    const variant = this.variant;
    const length = this.length;

    const slotsHTML = (start: number, end: number): string =>
      Array.from(
        { length: end - start },
        (_, i) =>
          `<rtg-input-otp-slot index="${start + i}"></rtg-input-otp-slot>`,
      ).join("");

    const group = (inner: string) =>
      `<rtg-input-otp-group>${inner}</rtg-input-otp-group>`;

    const sep = `<rtg-input-otp-separator></rtg-input-otp-separator>`;

    if (variant === "simple") {
      const half = Math.ceil(length / 2);

      return group(slotsHTML(0, half)) + sep + group(slotsHTML(half, length));
    }

    if (variant === "with_separator") {
      const third = Math.ceil(length / 3);

      return (
        group(slotsHTML(0, third)) +
        sep +
        group(slotsHTML(third, third * 2)) +
        sep +
        group(slotsHTML(third * 2, length))
      );
    }

    if (variant === "with_spacing") {
      return Array.from({ length }, (_, i) =>
        group(`<rtg-input-otp-slot index="${i}"></rtg-input-otp-slot>`),
      ).join("");
    }

    return group(slotsHTML(0, length));
  }

  // --------------------------------------------------
  // HANDLERS
  // --------------------------------------------------

  private handleComplete = async (value: string) => {
    this._value = value;
    // this._invalid = false;
    // this._error = "";
    // ------------------------------------------------
    // STANDALONE MODE (OLD BEHAVIOUR)
    // ------------------------------------------------
    console.log("handleComplete", {
      value,
      autoSubmit: this.autoSubmit,
      event: this.event,
    });
    if (this.autoSubmit && this.event) {
      const formData: Record<string, any> = {};

      formData[this.dataKey] = value;

      try {
        await Auth.processLoginflowEvent({
          event: this.event,
          data: formData,
        });
      } finally {
        this.emit("sb-otp:complete", {
          id: this.id,
          dataKey: this.dataKey,
          value,
          event: this.event,
        });
      }

      return;
    }

    // ------------------------------------------------
    // FORM FIELD MODE
    // ------------------------------------------------

    this.emit("sb-otp:complete", {
      id: this.id,
      dataKey: this.dataKey,
      value,
      event: null,
    });
  };

  private handleFocus = () => {
    this.emit("sb-otp:focus", {
      id: this.id,
      dataKey: this.dataKey,
    });
  };

  private handleBlur = () => {
    this.emit("sb-otp:blur", {
      id: this.id,
      dataKey: this.dataKey,
    });
  };

  private handleResend = async () => {
    if (!this.resendEvent) return;

    try {
      await Auth.processLoginflowEvent({
        event: this.resendEvent,
      });
    } finally {
      this.emit("sb-otp:resend", {
        id: this.id,
        event: this.resendEvent,
      });
    }
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  render() {
    const variant = this.variant || "digits_only";

    return html`
      <rtg-field ?disabled=${this.disabled} ?invalid=${this._invalid}>
        <rtg-input-otp
          part=${this.id ? this.id : "otp"}
          id=${this.dataKey ?? this.id ?? nothing}
          name=${this.name || this.dataKey || this.id}
          maxlength=${this.length}
          pattern=${variant === "digits_only" ? "\\d+" : nothing}
          data-key=${this.dataKey || ""}
          ?disabled=${this.disabled}
          .onComplete=${this.handleComplete}
          .onFocus=${this.handleFocus}
          .onBlur=${this.handleBlur}
          .onChange=${(value: string) => (this._value = value)}
        >
          ${unsafeStatic(this.buildOTPContentHTML())}
        </rtg-input-otp>
        ${this._invalid
          ? html`
              <rtg-field-error
                .errors=${[{ message: this._error }]}
              ></rtg-field-error>
            `
          : nothing}
      </rtg-field>
      ${this.resendButtonText
        ? html`
            <div class="sb-flex sb-justify-center">
              <rtg-typography-p>${this.resendText}</rtg-typography-p>

              <rtg-button
                variant="link"
                ?disabled=${this.disabled}
                @click=${this.handleResend}
              >
                ${this.resendButtonText}
              </rtg-button>
            </div>
          `
        : nothing}
    `;
  }
}
